import type { Curriculum } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { Edit2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { updateCurriculum } from '@/features/curriculums/curriculums-api';
import {
  type Locale,
  supportedLanguages,
} from '@/features/locale/supported-languages';
import { curriculumNamesSchema } from '../actions.schema';

function emptyNames(): Record<Locale, string> {
  return Object.fromEntries(
    supportedLanguages.map((locale) => [locale, '']),
  ) as Record<Locale, string>;
}

function EditCurriculumForm({
  curriculum,
  onSuccess,
}: {
  curriculum: Curriculum;
  onSuccess: () => void;
}) {
  const t = useTranslations('admin');
  const t_lang = useTranslations('languages');
  // The Next form (conform) started with empty inputs and required every name
  // to be re-entered on save; we keep that behaviour rather than pre-filling.
  const [names, setNames] = useState<Record<Locale, string>>(emptyNames);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = curriculumNamesSchema.safeParse(names);
    if (!submission.success) {
      setError(t('name_in', { locale: t_lang('en') }));
      return;
    }

    setPending(true);
    try {
      const result = await updateCurriculum(curriculum.id, submission.data);
      if (result.ok) {
        onSuccess();
      } else {
        setError(result.error);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {supportedLanguages.map((locale) => (
        <div key={locale} className="flex flex-col gap-2">
          <Label htmlFor={`edit-curriculum-${locale}`}>
            {t('name_in', { locale: t_lang(locale) })}
          </Label>
          <Input
            id={`edit-curriculum-${locale}`}
            type="text"
            value={names[locale]}
            onChange={(e) =>
              setNames((prev) => ({ ...prev, [locale]: e.target.value }))
            }
          />
        </div>
      ))}
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton label={t('save')} pending={pending} />
    </form>
  );
}

export default function EditCurriculumDialog({
  curriculum,
  onSaved,
}: {
  curriculum: Curriculum;
  onSaved: () => void;
}) {
  const t = useTranslations('admin');
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Edit2Icon size="16" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('edit', { what: t('curriculum').toLowerCase() })}
          </DialogTitle>
        </DialogHeader>
        <EditCurriculumForm
          curriculum={curriculum}
          onSuccess={() => {
            setOpen(false);
            onSaved();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
